import { useEffect, useCallback, useReducer } from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import DraggableModal from '../../generic/DraggableModal'
import Stack from '@mui/material/Stack'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment from 'moment'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import { backendPostWithoutRefresh } from '../../../helpers/callBackend'
import Typography from '@mui/material/Typography'

const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_TOTAL_PAGES: 'SET_TOTAL_PAGES',
  INCREMENT_RUNS_COMPLETED: 'INCREMENT_RUNS_COMPLETED',
  RUN_PAGE_SUCCESS: 'RUN_PAGE_SUCCESS',
  SET_RUN_DATE: 'SET_RUN_DATE',
  SET_MODAL_VISIBLE: 'SET_MODAL_VISIBLE',
}

const setLoading = (loading) => {
  return { type: ACTION_TYPES.SET_LOADING, payload: loading }
}

const setTotalPages = (columns) => {
  return { type: ACTION_TYPES.SET_TOTAL_PAGES, payload: columns }
}

const setRunDate = (runDate) => {
  return { type: ACTION_TYPES.SET_RUN_DATE, payload: runDate }
}

const setModalVisible = (visible) => {
  return {
    type: ACTION_TYPES.SET_MODAL_VISIBLE,
    payload: visible,
  }
}

const runPageSuccess = (runData) => {
  return {
    type: ACTION_TYPES.RUN_PAGE_SUCCESS,
    payload: runData,
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case ACTION_TYPES.SET_TOTAL_PAGES:
      const totalPages = action.payload.reduce((prevSum, column) => prevSum + column?.pages, 0)
      return {
        ...state,
        totalPages: totalPages,
      }

    case ACTION_TYPES.SET_RUN_DATE:
      return {
        ...state,
        runDate: action.payload,
      }

    case ACTION_TYPES.SET_MODAL_VISIBLE:
      return {
        ...state,
        modalVisible: action.payload,
      }

    case ACTION_TYPES.RUN_PAGE_SUCCESS:
      const newRunData = [...state.runData, action.payload]
      const loadingDone = newRunData.length >= state.totalPages
      return {
        ...state,
        runData: newRunData,
        loading: !loadingDone,
      }

    default:
      return state
  }
}

const RunReportModal = ({ reportName, columns, onRunComplete }) => {
  const initialState = {
    loading: false,
    modalVisible: false,
    runDate: moment().format('YYYY-MM-DD'),
    runData: [],
    totalPages: 0,
    runsCompleted: 0,
  }
  const [state, dispatch] = useReducer(reducer, initialState)

  const runPage = useCallback(
    async (columnId, pageNum) => {
      try {
        const runResp = await backendPostWithoutRefresh(
          '/api/reports/v2/report-builder-run/',
          {
            column_id: columnId,
            run_date: state.runDate,
            page_index: pageNum,
          },
          false,
          false
        )
        dispatch(runPageSuccess(runResp?.data?.data))
      } catch (error) {
        return
      }
    },
    [state]
  )

  const runColumn = useCallback(
    (column) => {
      try {
        if (column?.pages) {
          for (let pageNum = 1; pageNum <= column.pages; pageNum++) {
            runPage(column?.id, pageNum)
          }
        } else {
          runPage(column?.id, 0)
        }
      } catch (error) {
        return
      }
    },
    [runPage]
  )

  const runReport = useCallback(() => {
    try {
      columns.forEach((column) => {
        runColumn(column)
      })
    } catch (error) {
      errorMessage(error.message)
    }
  }, [dispatch, columns, runColumn])

  useEffect(() => {
    dispatch(setTotalPages(columns))
  }, [columns])

  useEffect(() => {
    if (!state.loading && state.runData.length > 0) {
      onRunComplete(state.runData)
      dispatch(setModalVisible(false))
    }
  }, [state.loading, state.runData])

  return (
    <>
      <LoadingButton
        disableElevation
        size='small'
        variant='contained'
        sx={{ fontSize: 14, textTransform: 'none' }}
        onClick={() => dispatch(setModalVisible(true))}
      >
        Run Report
      </LoadingButton>

      {state.modalVisible && (
        <DraggableModal
          modalTitle={`Run ${reportName}`}
          modalVisible={state.modalVisible}
          handleCancelModal={() => dispatch(setModalVisible(false))}
          content={
            <Stack justifyContent='center'>
              <Typography>Select a run date</Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  defaultValue={moment()}
                  maxDate={moment()}
                  onChange={(value) => {
                    dispatch(setRunDate(value.format('YYYY-MM-DD')))
                  }}
                />
              </LocalizationProvider>
            </Stack>
          }
          actions={
            <LoadingButton
              disableElevation
              loading={state.loading}
              size='small'
              variant='contained'
              sx={{ fontSize: 14, textTransform: 'none' }}
              onClick={runReport}
            >
              Run Report
            </LoadingButton>
          }
        />
      )}
    </>
  )
}

export default RunReportModal
